/**
 * X-BACKGROUND-001 — professional history and background, 18+ only.
 *
 * Asked only of someone who has already told us they have paid work
 * experience (`years_experience >= 1`). A student has no history to give,
 * and asking anyway produces a made-up field that then quietly colours
 * their whole result.
 *
 * Two answers, both safe to ask directly because neither has a socially
 * correct response:
 *
 *   1. FIELD — the world of work they've actually been paid in. A fact,
 *      not a virtue.
 *   2. INTENT — whether they want to go deeper in it, move sideways out of
 *      it, or leave it altogether. A statement of direction.
 *
 * Both enter the engine through the SAME bounded family-affinity channel
 * passions already use, and only when the learner says they want to stay
 * in or near the field. The rule that matters:
 *
 *   A FIELD SOMEONE WANTS OUT OF MUST NEVER BE BOOSTED.
 *
 * Eight years in risk is a reason to show someone what transfers into
 * interiors — it is not a reason to put more risk jobs in front of them.
 * So `switch` and `unsure` contribute no affinity at all, and no
 * eligibility gate reads any of this: a background must never delete a
 * career from the pool.
 *
 * CANON-PENDING: X-BACKGROUND-001 is not yet ratified in the Item Bank.
 * Recorded here until the Command Center carries it.
 */
import type { CareerIntent } from "./types";

export interface ProfessionalField {
  id: string;
  label: string;
  hint?: string;
  /** Career families this background sits inside. */
  families: string[];
  /**
   * What genuinely carries into other work. Second person, concrete, and
   * never flattering — these are shown back to the learner verbatim.
   */
  transferables: string[];
}

export const OTHER_FIELD_ID = "other";

export const PROFESSIONAL_FIELDS: ProfessionalField[] = [
  {
    id: "finance",
    label: "Finance, banking or accounting",
    hint: "Books, capital, audit, treasury",
    families: ["Finance & Accounting", "Banking", "FinTech & Digital Finance", "Economics & Policy"],
    transferables: [
      "reading a budget and knowing when it's wrong",
      "working to a hard deadline other people depend on",
      "explaining a number to someone who doesn't want to hear it",
    ],
  },
  {
    id: "risk",
    label: "Risk, audit or compliance",
    hint: "Controls, regulation, assurance",
    families: ["Legal & Compliance", "Finance & Accounting", "Banking", "Government & Public Sector"],
    transferables: [
      "spotting what will go wrong before it does",
      "holding a standard when there's pressure to let it slide",
      "documenting a decision so it survives being questioned",
    ],
  },
  {
    id: "tech",
    label: "Technology, software or data",
    hint: "Engineering, analytics, IT, product",
    families: ["Technology & Software", "Data & Analytics", "Product Management", "Cybersecurity", "AI & Emerging Technologies"],
    transferables: [
      "breaking a vague problem into steps that can be built",
      "learning a new tool fast, without a course",
      "shipping something imperfect and improving it",
    ],
  },
  {
    id: "sales",
    label: "Sales, marketing or business development",
    families: ["Sales & Business Development", "Marketing & Growth", "Consulting & Strategy", "Entrepreneurship & Startups"],
    transferables: [
      "reading what a client actually wants, not what they said",
      "handling a no without taking it personally",
      "pitching an idea to people who owe you nothing",
    ],
  },
  {
    id: "ops",
    label: "Operations, admin or HR",
    families: ["Business & Administration", "Human Resources", "Transportation & Logistics", "Manufacturing & Industrial"],
    transferables: [
      "keeping many moving parts on schedule",
      "writing a process someone else can follow",
      "sorting out a mess quietly, before it becomes a crisis",
    ],
  },
  {
    id: "health",
    label: "Healthcare or care work",
    families: ["Healthcare & Medicine", "Health & Social Care", "Pharmaceuticals & Biotech"],
    transferables: [
      "staying calm while someone else isn't",
      "making a call with incomplete information",
      "long hours on your feet without dropping standards",
    ],
  },
  {
    id: "education",
    label: "Teaching, training or academia",
    families: ["Education & Training", "Coaching & Professional Development", "Science & Research"],
    transferables: [
      "explaining something hard to someone who's stuck",
      "planning weeks of work backwards from an outcome",
      "reading a room and changing tack mid-way",
    ],
  },
  {
    id: "creative",
    label: "Design, media or content",
    families: ["Creative Arts & Media", "Design & User Experience", "Journalism & Media", "Creator Economy & Digital"],
    transferables: [
      "taking feedback on work you made yourself",
      "finishing to a brief and a deadline",
      "showing an idea before it exists",
    ],
  },
  {
    id: "engineering",
    label: "Engineering, construction or manufacturing",
    families: ["Engineering (Non-IT)", "Engineering", "Skilled Trades & Construction", "Real Estate & Construction", "Manufacturing & Industrial"],
    transferables: [
      "reading a drawing or a spec and finding the error",
      "working to tolerance, where close enough isn't",
      "coordinating trades and suppliers who don't report to you",
    ],
  },
  {
    id: "legal",
    label: "Law or public service",
    families: ["Legal & Compliance", "Government & Public Sector", "Public Service & Safety", "Social Work & NGO"],
    transferables: [
      "building an argument that holds up to scrutiny",
      "reading long documents for the one line that matters",
      "acting for someone else's interest ahead of your own view",
    ],
  },
  {
    id: "hospitality",
    label: "Hospitality, retail or services",
    families: ["Hospitality & Tourism", "Retail & E-commerce", "Personal & Community Services", "Sports & Fitness"],
    transferables: [
      "dealing with people all day and staying pleasant",
      "handling a rush without losing the thread",
      "selling and serving in the same breath",
    ],
  },
  {
    id: "trades",
    label: "A skilled trade or hands-on work",
    families: ["Blue-Collar & Trade", "Skilled Trades & Construction", "Manufacturing & Industrial", "Agriculture & Food Production"],
    transferables: [
      "working with your hands to a finish that shows",
      "quoting a job and making the numbers work",
      "fixing what's in front of you with what you have",
    ],
  },
  {
    id: OTHER_FIELD_ID,
    label: "Something else",
    hint: "Nothing is scored from this — we'll rely on your answers instead",
    families: [],
    transferables: [
      "years of showing up and being relied on",
      "learning a workplace's unwritten rules",
    ],
  },
];

export const CAREER_INTENTS: { value: CareerIntent; label: string; hint: string }[] = [
  { value: "deepen", label: "Go deeper in it", hint: "Same field, more of it" },
  { value: "adjacent", label: "Move sideways", hint: "Nearby work, using what I've built" },
  { value: "switch", label: "Change direction properly", hint: "Something genuinely different" },
  { value: "unsure", label: "That's what I'm here to find out", hint: "Nothing is assumed either way" },
];

export function fieldById(id: string | undefined): ProfessionalField | undefined {
  return id ? PROFESSIONAL_FIELDS.find((f) => f.id === id) : undefined;
}

/**
 * Family affinity from the background — and ONLY when the learner wants to
 * stay in or near the field. `deepen` counts as a named interest; `adjacent`
 * counts half, so it can never reach the strong bonus on its own. `switch`
 * and `unsure` return nothing at all.
 */
export function backgroundFamilyCounts(
  fieldId: string | undefined,
  intent: CareerIntent | undefined,
): Map<string, number> {
  const out = new Map<string, number>();
  const field = fieldById(fieldId);
  if (!field || field.id === OTHER_FIELD_ID) return out;
  const weight = intent === "deepen" ? 1 : intent === "adjacent" ? 0.5 : 0;
  if (weight === 0) return out;
  for (const f of field.families) out.set(f, weight);
  return out;
}

/** What carries across, in the learner's own terms. Empty when unasked. */
export function transferableSkills(fieldId: string | undefined): string[] {
  return fieldById(fieldId)?.transferables ?? [];
}

function yearsPhrase(years: number | undefined): string {
  if (!years || years < 1) return "your time";
  if (years === 1) return "a year";
  if (years >= 40) return "40 years or more";
  return `${years} years`;
}

/** One honest line for the result screen. Null when we never asked. */
export function backgroundResultNote(
  fieldId: string | undefined,
  intent: CareerIntent | undefined,
  years: number | undefined,
): string | null {
  const field = fieldById(fieldId);
  if (!field) return null;
  const where = field.id === OTHER_FIELD_ID ? "work" : field.label.toLowerCase();
  const time = yearsPhrase(years);
  switch (intent) {
    case "deepen":
      return `You've given ${time} to ${where} and you want to go deeper, so we've kept that world close to the top — and shown you where it can still take you.`;
    case "adjacent":
      return `You've given ${time} to ${where} and you'd move sideways, so we've leaned towards work that uses what you already have without starting again.`;
    case "switch":
      return `You've given ${time} to ${where} and you want a real change, so we have deliberately NOT pushed more of it at you. What you built there still counts — see what transfers, below.`;
    case "unsure":
      return `You've given ${time} to ${where} and you're still deciding whether to stay. We've assumed nothing either way, so this list isn't weighted towards it.`;
    default:
      return null;
  }
}

/**
 * The "so what do I do on Monday" line. Concrete, honest about the cost of
 * a switch, and never a promise.
 */
export function nextStepNote(
  fieldId: string | undefined,
  intent: CareerIntent | undefined,
  targetName: string | null,
): string | null {
  if (!fieldById(fieldId)) return null;
  if (intent !== "switch" && intent !== "adjacent") return null;
  const target = targetName ? `into ${targetName}` : "into your top match";
  return intent === "adjacent"
    ? `To move ${target}, start with the shortest credible route on that card and one real piece of work you can show. Your experience already covers the rest.`
    : `A proper change ${target} usually needs three things: the cheapest qualifying route on that card, one portfolio piece you made yourself, and someone already doing it who'll look at it. Your years count for the third.`;
}
